import { Product } from "../pos"
import { ProductChoice } from "./evaluated-service-bike-part"
import {
  FreshServiceProposal,
  FreshServiceProposalWash,
  FreshServiceProposalWashAlternatives,
  ServiceProposalFee,
  ServiceProposalManualAction,
  ServiceProposalPackage,
  ServiceProposalPartAction,
  SubmitFreshServiceProposal,
} from "./fresh-service-proposal"
import { ProposalItemSource } from "./proposal-item-source"
import { ServiceAction } from "./service-part-action"

/**
 * Turns a proposal into the four groups the redesigned customer screen renders:
 * "Valbara åtgärder", "Obligatoriska åtgärder", "Redan godkända åtgärder" and
 * "Avgifter". Packages, part actions, manual actions and washes are interleaved
 * inside a group, so they share one discriminated-union item type.
 *
 * A deselected item keeps its place in the list and merely renders as
 * "Borttagen", so the selection is only consulted for the one case where a row
 * would be a lie: work a selected package already pays for.
 */

export type ProposalItem =
  | { kind: "PACKAGE"; id: string; package: ServiceProposalPackage }
  | { kind: "PART_ACTION"; id: string; partAction: ServiceProposalPartAction }
  | {
      kind: "MANUAL_ACTION"
      id: string
      manualAction: ServiceProposalManualAction
    }
  | { kind: "WASH"; id: string; wash: FreshServiceProposalWash }
  | {
      kind: "WASH_ALTERNATIVES"
      id: string
      washAlternatives: FreshServiceProposalWashAlternatives
    }
  | { kind: "FEE"; id: string; fee: ServiceProposalFee }

export interface GroupedProposalItems {
  /** Freely selectable — the customer may opt out. */
  optional: ProposalItem[]
  /** Required by the mechanic; shown with a lock and not deselectable. */
  mandatory: ProposalItem[]
  /** Agreed at drop-off; locked and already paid for in the agreement. */
  receptionApproved: ProposalItem[]
  fees: ProposalItem[]
}

/** Part ids that belong to a package, whether or not it is selected. */
const getPackagePartIds = (source: ProposalItemSource): Set<string> => {
  const ids = new Set<string>()

  for (const servicePackage of source.packages) {
    for (const action of servicePackage.partActions) ids.add(action.partId)
  }

  return ids
}

/**
 * The proposal submission's answers to what `groupProposalItems` and
 * `getPendingChoiceIds` ask for. An addon order answers the same questions from
 * its own payload, which is why they take sets rather than a submission.
 */
export const getStandalonePartIds = (
  values: SubmitFreshServiceProposal,
): Set<string> =>
  new Set(values.selectedPartActions.map((selected) => selected.partId))

/** Every part the submission buys, standalone or inside a package. */
export const getSelectedPartIds = (
  values: SubmitFreshServiceProposal,
): Set<string> => {
  const ids = getStandalonePartIds(values)
  for (const selectedPackage of values.selectedPackages) {
    for (const selected of selectedPackage.selectedPartActions) {
      ids.add(selected.partId)
    }
  }
  return ids
}

export const getSelectedManualActionIds = (
  values: SubmitFreshServiceProposal,
): Set<string> =>
  new Set(values.selectedManualActions.map((selected) => selected.id))

/**
 * `standalonePartIds` is the set of parts the customer is buying on their own
 * rather than through a package. Taking it as a set rather than a submission
 * keeps this usable from both approval screens, whose payloads differ.
 */
export const groupProposalItems = (
  source: ProposalItemSource,
  standalonePartIds: ReadonlySet<string>,
): GroupedProposalItems => {
  const optional: ProposalItem[] = []
  const mandatory: ProposalItem[] = []
  const receptionApproved: ProposalItem[] = []

  for (const servicePackage of source.packages) {
    const item: ProposalItem = {
      kind: "PACKAGE",
      id: servicePackage.originalPackageId,
      package: servicePackage,
    }
    if (servicePackage.isApprovedViaReception) {
      receptionApproved.push(item)
    } else {
      optional.push(item)
    }
  }

  const packagePartIds = getPackagePartIds(source)

  for (const partAction of source.partActions) {
    // A package's work is listed twice — inside the package and standalone —
    // and the package card speaks for it either way: as its contents while
    // selected, and behind "N åtgärder bortvalda" once it is not. So the
    // standalone row only earns its place when the action has actually left the
    // package and is being charged on its own.
    if (
      packagePartIds.has(partAction.partId) &&
      !standalonePartIds.has(partAction.partId)
    ) {
      continue
    }
    // DO_NOTHING entries exist to carry the mechanic's comment, not work to buy.
    if (partAction.action === ServiceAction.DO_NOTHING) continue

    const item: ProposalItem = {
      kind: "PART_ACTION",
      id: partAction.partId,
      partAction,
    }
    if (partAction.isApprovedViaReception) {
      receptionApproved.push(item)
    } else if (partAction.isMandatory) {
      mandatory.push(item)
    } else {
      optional.push(item)
    }
  }

  for (const manualAction of source.manualActions) {
    const item: ProposalItem = {
      kind: "MANUAL_ACTION",
      id: manualAction.id,
      manualAction,
    }
    if (manualAction.isMandatory) {
      mandatory.push(item)
    } else {
      optional.push(item)
    }
  }

  if (source.washAlternatives) {
    const item: ProposalItem = {
      kind: "WASH_ALTERNATIVES",
      id: source.washAlternatives.id,
      washAlternatives: source.washAlternatives,
    }
    if (source.washAlternatives.isMandatory) {
      mandatory.push(item)
    } else {
      optional.push(item)
    }
  } else if (source.wash) {
    optional.push({
      kind: "WASH",
      id: source.wash.originalWashId,
      wash: source.wash,
    })
  }

  const fees: ProposalItem[] = source.fees
    .filter((fee) => fee.cost > 0)
    .map((fee) => ({
      kind: "FEE",
      id: fee.snapshotId,
      fee,
    }))

  return { optional, mandatory, receptionApproved, fees }
}

/**
 * An action by part id, wherever the proposal describes it. A part covered by a
 * package is usually described twice — standalone and inside the package — and
 * the standalone entry wins, because it is the one carrying the labour price.
 *
 * Returns nothing for a part the proposal never mentions. Every evaluated part
 * is graded in "Cykelskick", including the ones sent to a physical workshop,
 * which never reach the customer's proposal at all.
 */
export const findProposalPartAction = (
  source: ProposalItemSource,
  partId: string,
): ServiceProposalPartAction | undefined =>
  source.partActions.find((action) => action.partId === partId) ??
  source.packages
    .flatMap((servicePackage) => servicePackage.partActions)
    .find((action) => action.partId === partId)

/**
 * Every action in the proposal that offers the customer a product choice, once
 * each: a part carrying a choice is listed both standalone and inside its
 * package, and the customer answers the question a single time.
 */
export const getActionsWithProductChoices = (
  source: ProposalItemSource,
): ProposalItem[] => {
  const items: ProposalItem[] = []
  const seenPartIds = new Set<string>()

  const collectFromPartAction = (
    partAction: ServiceProposalPartAction,
  ): void => {
    if (partAction.productChoices.length === 0) return
    if (seenPartIds.has(partAction.partId)) return
    seenPartIds.add(partAction.partId)
    items.push({ kind: "PART_ACTION", id: partAction.partId, partAction })
  }

  for (const partAction of source.partActions) collectFromPartAction(partAction)
  for (const manualAction of source.manualActions) {
    if (manualAction.productChoices.length > 0) {
      items.push({ kind: "MANUAL_ACTION", id: manualAction.id, manualAction })
    }
  }
  for (const servicePackage of source.packages) {
    for (const partAction of servicePackage.partActions) {
      collectFromPartAction(partAction)
    }
  }

  return items
}

/**
 * Ids of every product choice the customer is currently being asked to make.
 * Only choices belonging to a *selected* action count — deselecting the action
 * removes the question along with the work.
 */
export const getPendingChoiceIds = (
  source: ProposalItemSource,
  selected: {
    partIds: ReadonlySet<string>
    manualIds: ReadonlySet<string>
  },
): string[] => {
  const selectedPartIds = selected.partIds
  const selectedManualIds = selected.manualIds

  // A set, because a part listed both standalone and inside a package asks the
  // same question twice and the count drives "N val att göra!".
  const choiceIds = new Set<string>()

  const collectFromPartAction = (
    partAction: ServiceProposalPartAction,
  ): void => {
    if (!selectedPartIds.has(partAction.partId)) return
    for (const choice of partAction.productChoices) choiceIds.add(choice.id)
  }

  for (const partAction of source.partActions) collectFromPartAction(partAction)
  for (const servicePackage of source.packages) {
    for (const partAction of servicePackage.partActions) {
      collectFromPartAction(partAction)
    }
  }
  for (const manualAction of source.manualActions) {
    if (!selectedManualIds.has(manualAction.id)) continue
    for (const choice of manualAction.productChoices) choiceIds.add(choice.id)
  }

  return [...choiceIds]
}

const grossPrice = (product: Product): number =>
  product.price * (product.quantity ?? 1)

/**
 * Cheapest alternative in a choice group. It is what an undecided choice is
 * priced at on the customer screen, so "Från X kr" is a floor rather than a
 * number that can still go down.
 */
export const getCheapestAlternative = (choice: ProductChoice): Product =>
  [choice.recommendedAlternative, ...choice.otherAlternatives].reduce(
    (cheapest, product) =>
      grossPrice(product) < grossPrice(cheapest) ? product : cheapest,
  )

/** Cheapest alternative in a choice group — drives the "Från X kr" label. */
export const getCheapestAlternativePrice = (choice: ProductChoice): number =>
  grossPrice(getCheapestAlternative(choice))

/**
 * Ids of the product choices the customer has actually answered. A choice the
 * customer answered and then dropped — by deselecting the action or the package
 * it belongs to — is not in here: re-adding the work starts the decision over,
 * and treating it as answered would submit a product nobody picked.
 */
export const getDecidedChoiceIds = (
  values: SubmitFreshServiceProposal,
): Set<string> => {
  const decided = new Set<string>()

  const collect = (productDecisions: Record<string, string>): void => {
    for (const choiceId of Object.keys(productDecisions)) decided.add(choiceId)
  }

  for (const selected of values.selectedPartActions)
    collect(selected.productDecisions)
  for (const selected of values.selectedManualActions)
    collect(selected.productDecisions)
  for (const selectedPackage of values.selectedPackages) {
    for (const selected of selectedPackage.selectedPartActions) {
      collect(selected.productDecisions)
    }
  }

  return decided
}

/**
 * Deselecting a package moves the work the customer cannot opt out of — the
 * mandatory actions and the ones agreed at drop-off — out of the package and
 * into the standalone list. Those parts are described only inside the package,
 * so anything resolving `selectedPartActions` against `proposal.partActions`
 * (the receipt mapper, most importantly) would fail to find them. This lifts
 * them up so the proposal describes everything the selection refers to.
 */
export const withMovedOutPackageActions = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): FreshServiceProposal => {
  const known = new Set(proposal.partActions.map(({ partId }) => partId))
  const unknownPartIds = values.selectedPartActions
    .map(({ partId }) => partId)
    .filter((partId) => !known.has(partId))
  if (unknownPartIds.length === 0) return proposal

  const insidePackages = new Map<string, ServiceProposalPartAction>()
  for (const servicePackage of proposal.packages) {
    for (const action of servicePackage.partActions) {
      insidePackages.set(action.partId, action)
    }
  }

  const additions: ServiceProposalPartAction[] = []
  for (const partId of unknownPartIds) {
    const action = insidePackages.get(partId)
    if (action) additions.push(action)
  }
  if (additions.length === 0) return proposal

  return { ...proposal, partActions: [...proposal.partActions, ...additions] }
}

/**
 * A selection can list a part under a package that the proposal no longer
 * puts in that package: the mechanic removed it from the package in the work
 * order editor after the customer answered, which prunes the proposal's own
 * package snapshot. The receipt mapper only looks a package's parts up inside
 * the package, so such a part would crash it. It is dropped rather than moved
 * to the standalone list, since it is no longer on the order at all.
 */
export const withPackageSelectionsMatchingProposal = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  const packagePartIds = new Map(
    proposal.packages.map((servicePackage) => [
      servicePackage.originalPackageId,
      new Set(servicePackage.partActions.map(({ partId }) => partId)),
    ]),
  )

  let hasChanged = false
  const selectedPackages = values.selectedPackages.map((selectedPackage) => {
    const partIds = packagePartIds.get(selectedPackage.originalPackageId)
    if (!partIds) return selectedPackage

    const kept = selectedPackage.selectedPartActions.filter((action) =>
      partIds.has(action.partId),
    )
    if (kept.length === selectedPackage.selectedPartActions.length) {
      return selectedPackage
    }
    hasChanged = true
    return { ...selectedPackage, selectedPartActions: kept }
  })
  if (!hasChanged) return values

  return { ...values, selectedPackages }
}
